/**
 * CollectionActivityDialog — the audit trail for a single collection.
 *
 * Shows this collection's audit events (monitoring started/stopped, updated,
 * deleted) newest-first, with actor + timestamp. Gated by the caller on
 * firm.audit_view; the endpoint enforces the same and is org-scoped.
 */
import React from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Badge } from '../../../ui/badge';
import { Timestamp } from '../../../ui/timestamp';
import { useAuditEvents } from '../../../../hooks/useAuditEvents';

interface CollectionActivityDialogProps {
  collectionId: string;
  collectionName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CollectionActivityDialog: React.FC<CollectionActivityDialogProps> = ({
  collectionId,
  collectionName,
  open,
  onOpenChange,
}) => {
  // Only fetch while the dialog is open. resource_id is polymorphic, so scope
  // it to collection events.
  const { data: events, isLoading, error } = useAuditEvents({
    resourceType: 'collection',
    resourceId: collectionId,
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="truncate">Activity — {collectionName}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-1 overflow-y-auto">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading activity…</p>
          )}
          {error && (
            <p className="text-sm text-destructive">Couldn&apos;t load activity.</p>
          )}
          {events && events.length === 0 && (
            <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
          )}
          {events?.map((e) => (
            <div
              key={e.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-3 py-2 text-sm"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Badge variant="secondary">{e.action}</Badge>
                <span className="truncate text-muted-foreground">
                  {e.actorName
                    ? `by ${e.actorName}`
                    : e.actorUserId
                      ? `by ${e.actorUserId.slice(0, 8)}`
                      : 'system'}
                </span>
              </div>
              <Timestamp value={e.createdAt} format="relative" />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CollectionActivityDialog;
